export type CorrelationSource = 'tci' | 'edgate' | 'ai';

export interface ModelCorrelation {
  category: string | null;
  description: string | null;
  /** Enum key from tags.grade — "K" through "12". Null outside grade-scoped categories. */
  grade: string | null;
  /** EdGate rows are soft-hidden rather than deleted; the manager shows them with an Unhide action. */
  hidden: boolean;
  /** model_tags.id — the only stable row identity; (model_type, model_id, tag_id) isn't unique. */
  id: number;
  source: CorrelationSource;
  source_label: string;
  standard: string | null;
  /** From tags.state_id, not model_tags.state_id — the latter is null on legacy EdGate rows. */
  state_id: number | null;
  state_name: string | null;
  tag_id: number;
}

/** Falls back to the tag id so a row with no standard code can still name itself. */
export const correlationLabel = ({ standard, tag_id: tagId }: ModelCorrelation) => (
  standard ?? `correlation ${tagId}`
);

/** Fixed list from the server, so the dropdown offers categories the item has no rows in. */
export interface CorrelationCategory {
  has_grade_scoping: boolean;
  has_state_scoping: boolean;
  label: string;
  value: string;
}

export interface ModelCorrelationsResponse {
  data: ModelCorrelation[];
  /** `count` is the visible total; `data` includes hidden rows, which the footer reports separately. */
  meta: { categories: CorrelationCategory[]; count: number; hidden_count: number };
}

/** A standard the item has no correlation to yet, so the tag is its only identity. */
export interface CandidateStandard {
  description: string | null;
  standard: string | null;
  tag_id: number;
}

export interface CandidateStandardsResponse {
  data: CandidateStandard[];
  /** The whole pool, not the page — the panel says how many candidates are left to load. */
  meta: { count: number };
}

export const candidateLabel = ({ standard, tag_id: tagId }: CandidateStandard) => (
  standard ?? `standard ${tagId}`
);

export type TagManagerVariant = 'inline' | 'card' | 'panel';

/** Empty string means "no filter applied" for every control. */
export const NO_FILTER = '';

export interface Filters {
  category: string;
  grade: string;
  search: string;
  state: string;
}

/**
 * AIDEV-NOTE: Mirrors Tag's `grade` enum (app/models/tag.rb).
 * If grades ever stop being that enum update this list in the same change, or have the
 * server send grade options alongside meta.categories and delete this constant.
 */
export const GRADE_ORDER = ['K', ...Array.from({ length: 12 }, (_, i) => String(i + 1))];
